import { Messages } from '~src/codegen/Localize';
import { showAddCredentialAuthModeDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { OAuthEditModelLoader } from '~src/connectivity/components/EditConnection/utils/OAuthEditModelLoader';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';

// W-22483245: the result of adding a new OAuth account from the WIF-eligible edit dialog: the freshly
// created credential id plus the reloaded account list so the caller can update the row in one write.
export interface IAddOAuthAccountResult {
  credentialId: string | undefined;
  credentials: IOAuthCredential[];
}

export namespace AddOAuthAccountFlow {

  // Run the add-account flow for a WIF-eligible connection (googlebigquery, or snowflake when OAuth):
  // the user picks OAuth or a service-account file in the add-credential dialog, we create the
  // credential, then reload this connection's account list so the new account can be selected.
  export async function addAccount(connection: EditableConnection): Promise<IAddOAuthAccountResult> {
    const type = connection.connectionType;
    let newCredentialId: string | undefined;

    await showAddCredentialAuthModeDialog(type, async (_props, state, updateDialogState) => {
      if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
        newCredentialId = await attemptScopedOAuth(connection);
      } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
        const result = await AddServiceAccountFileAction.addServiceAccountFile(state.connAuthState.file, type, updateDialogState);
        newCredentialId = result.id;
      }
    });

    const reloaded = await OAuthEditModelLoader.load([connection]);
    const credentials = reloaded.credentials;
    // Only keep the new id if the reloaded list actually contains it; otherwise leave the account
    // unselected rather than pointing at a credential the picker cannot show.
    const credentialId = credentials.some((c) => c.id === newCredentialId) ? newCredentialId : undefined;
    return { credentialId, credentials };
  }

  // W-23640221: snowflake's reloaded account list (OAuthEditModelLoader.load) filters credentials on
  // instanceUrl / role / oauthConfigId / server, so the OAuth flow must be scoped to those values or a
  // snowflake account added against a non-default role is created unscoped, filtered out, and silently
  // discarded. googlebigquery does not filter on those fields, so it keeps its bare attemptAuth(type)
  // call to preserve its exact pre-existing credential identity at creation time.
  function attemptScopedOAuth(connection: EditableConnection): Promise<string> {
    const type = connection.connectionType;
    const details = connection.connectionDetails;
    if (type === 'snowflake' && details.type === 'database') {
      // A snowflake connection with a default role stores role='', which the reload matches exactly.
      const role = details.role ?? '';
      return AddOAuthCredentialAction.attemptAuth(type, details.instanceUrl, role, details.oauthConfigId, details.serverName);
    }
    return AddOAuthCredentialAction.attemptAuth(type);
  }
}
